import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, imgUrl, money } from "@/lib/api";
import {
  Droplets, School, Wheat, Cpu, Sun, HeartHandshake, ArrowRight, Heart,
  MapPin, Users, ShieldCheck, Sprout,
} from "lucide-react";

const FOCUS = [
  { icon: Droplets, t: "Water & Sanitation", d: "Clean water access, wells, safe sanitation and hygiene — the foundation of a child's health and a family's stability." },
  { icon: School, t: "Education & Schools", d: "Classrooms, learning materials, teacher support and scholarships so every child can learn, read and dream." },
  { icon: Wheat, t: "Agriculture & Food Security", d: "Sustainable farming, nutrition and food-security programs that help families feed their children year-round." },
  { icon: Cpu, t: "Technology & Connectivity", d: "Computers, internet access and digital skills that connect communities to knowledge and opportunity." },
  { icon: Sun, t: "Clean Energy", d: "Reliable, affordable energy — solar and beyond — to power homes, schools and clinics." },
  { icon: HeartHandshake, t: "Community Development", d: "Infrastructure, local partnerships and capacity-building that strengthen whole communities from within." },
];

const CRITERIA = [
  { icon: MapPin, t: "Real, local need", d: "We prioritise communities facing the greatest barriers to safety, health and opportunity." },
  { icon: Users, t: "Community partnership", d: "Projects are chosen with local leaders and families — never imposed from the outside." },
  { icon: ShieldCheck, t: "Family preservation", d: "Wherever possible, we strengthen families and communities so children can grow up where they belong." },
  { icon: Sprout, t: "Lasting impact", d: "We favour sustainable solutions that keep serving a community long after a project is complete." },
];

function CIProjectCard({ p, donate }) {
  const statusMap = {
    planning: ["Planning", "bg-gray-100 text-gray-600"],
    in_progress: ["In progress", "bg-[#E0A526]/20 text-[#8a6d18]"],
    completed: ["Completed", "bg-[#16A34A]/15 text-[#16A34A]"],
  };
  const [label, cls] = statusMap[p.status] || statusMap.planning;
  const hasGoal = Number(p.goal) > 0;
  const pct = hasGoal ? Math.min(100, Math.round((Number(p.raised) / Number(p.goal)) * 100)) : (Number(p.progress) || 0);
  const showBar = hasGoal || (Number(p.progress) || 0) > 0;
  const link = p.gofundme_url || donate;
  return (
    <article className="bg-white border border-[#1F4D2E]/10 rounded-2xl overflow-hidden flex flex-col" data-testid={`ci-project-${p.id}`}>
      {p.image_url && <div className="aspect-[4/3] overflow-hidden"><img src={imgUrl(p.image_url)} alt={p.title} className="w-full h-full object-cover" loading="lazy" /></div>}
      <div className="p-7 flex flex-col flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#C1272D]">{p.focus_area}</span>
          <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{label}</span>
        </div>
        <h3 className="font-display text-2xl font-semibold text-[#1F4D2E] mb-1">{p.title}</h3>
        {p.location && <p className="text-sm text-[#4F6B54]/80 inline-flex items-center gap-1 mb-3"><MapPin size={14} /> {p.location}</p>}
        {p.summary && <p className="text-[#4F6B54] leading-relaxed text-[15px] mb-5">{p.summary}</p>}
        {showBar && (
          <div className="mb-5">
            <div className="h-2 rounded-full bg-[#E7EDE2] overflow-hidden"><div className="h-full bg-[#1F4D2E]" style={{ width: `${pct}%` }} /></div>
            <div className="flex justify-between text-xs text-[#4F6B54] mt-2">
              {hasGoal ? <><span>{money(p.raised)} raised</span><span>of {money(p.goal)}</span></> : <span>{pct}% complete</span>}
            </div>
          </div>
        )}
        {p.updates && p.updates.length > 0 && (
          <details className="mb-5 group" data-testid={`ci-project-updates-${p.id}`}>
            <summary className="cursor-pointer text-sm font-semibold text-[#1F4D2E] hover:text-[#C1272D] list-none">Progress updates ({p.updates.length}) ▾</summary>
            <ul className="mt-3 space-y-3 border-l-2 border-[#E0A526]/50 pl-4">
              {[...p.updates].reverse().map((u, i) => (
                <li key={i}>
                  <p className="text-xs text-[#4F6B54]/70">{u.date ? new Date(u.date).toLocaleDateString() : ""}</p>
                  {u.title && <p className="font-semibold text-[#1F4D2E] text-sm">{u.title}</p>}
                  {u.body && <p className="text-[#4F6B54] text-sm leading-relaxed whitespace-pre-line">{u.body}</p>}
                </li>
              ))}
            </ul>
          </details>
        )}
        <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`ci-project-support-${p.id}`} className="mt-auto inline-flex items-center gap-2 text-[#C1272D] font-semibold hover:gap-3 transition-all">Support this project <ArrowRight size={17} /></a>
      </div>
    </article>
  );
}

export default function CommunityImpactPage() {
  const [donate, setDonate] = React.useState("https://gofund.me/15d8987b7");
  const [projects, setProjects] = React.useState([]);
  React.useEffect(() => {
    api.get("/settings").then(({ data }) => data.gofundme_url && setDonate(data.gofundme_url)).catch(() => {});
    api.get("/community-projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="community-impact-page">
      {/* HERO */}
      <section className="relative bg-[#0F2E1B] text-white overflow-hidden">
        <span className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-[#E0A526]/10" />
        <span className="absolute -bottom-28 -left-16 w-80 h-80 rounded-full bg-[#3d7a52]/20" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E0A526] mb-5">Refugium Community Impact</p>
            <h1 className="font-display text-5xl sm:text-6xl font-bold leading-[0.98] tracking-tight">Stronger communities, alongside safe homes.</h1>
            <p className="text-lg text-[#E7EDE2]/90 mt-6 leading-relaxed">Protecting children is our heart — but children thrive when their whole community does. Refugium Community Impact is our long-term commitment to sustainable development across water, education, agriculture, technology, energy and community-building in Ethiopia.</p>
          </motion.div>
        </div>
      </section>

      {/* HONEST INTRO */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16 text-center" data-testid="ci-intro">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Where we're headed</p>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">We are just getting started — and building the right way.</h2>
        <p className="text-[#4F6B54] mt-5 leading-relaxed">These are the focus areas we are committed to as Refugium Ethiopia grows. We are not yet running active community projects, and we will only report real work as it genuinely begins. Every initiative below reflects our future direction, shaped together with the communities we serve.</p>
      </section>

      {/* REAL PROJECTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="ci-projects">
        <div className="max-w-2xl mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Our projects</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">Follow real progress on the ground.</h2>
        </div>
        {projects.length === 0 ? (
          <div className="border border-dashed border-[#1F4D2E]/25 rounded-2xl p-12 text-center bg-[#FBF8F0]" data-testid="ci-projects-empty">
            <Sprout size={40} className="text-[#1F4D2E]/40 mx-auto mb-4" />
            <p className="font-display text-2xl text-[#1F4D2E] mb-2">Our first community project will appear here.</p>
            <p className="text-[#4F6B54] max-w-xl mx-auto">We only publish projects once real work begins. When we break ground on our first initiative, you'll be able to follow its progress and updates right here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((p) => <CIProjectCard key={p.id} p={p} donate={donate} />)}
          </div>
        )}
      </section>

      {/* FOCUS AREAS */}
      <section className="bg-[#FBF8F0] border-y border-[#1F4D2E]/10" data-testid="ci-focus">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-2xl mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Our focus areas</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">Six ways we'll help communities thrive.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FOCUS.map((f, i) => (
              <motion.div key={f.t} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white border border-[#1F4D2E]/10 rounded-xl p-8 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 relative" data-testid={`ci-focus-${i}`}>
                <span className="absolute top-6 right-6 text-[10px] tracking-[0.15em] uppercase font-bold text-[#4F6B54]/50">Future program</span>
                <span className="w-14 h-14 rounded-full bg-[#1F4D2E]/10 flex items-center justify-center text-[#1F4D2E] mb-5"><f.icon size={26} /></span>
                <h3 className="font-display text-2xl font-semibold text-[#1F4D2E] mb-2">{f.t}</h3>
                <p className="text-[#4F6B54] leading-relaxed">{f.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE CHOOSE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="ci-approach">
        <div className="max-w-2xl mb-14">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Our approach</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">How we'll choose where to work.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {CRITERIA.map((c, i) => (
            <motion.div key={c.t} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-[#E7EDE2] rounded-xl p-7" data-testid={`ci-criteria-${i}`}>
              <span className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-[#1F4D2E] mb-4"><c.icon size={22} /></span>
              <h3 className="font-display text-xl font-semibold text-[#1F4D2E] mb-2">{c.t}</h3>
              <p className="text-[#4F6B54] text-sm leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SEPARATION FROM MERCY MADE */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24" data-testid="ci-vs-mercymade">
        <div className="bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-2xl p-8 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-2">Community Impact</p>
            <p className="text-[#4F6B54] leading-relaxed text-[15px]">Sustainable development — water, education, agriculture, technology, energy and community infrastructure that strengthens whole communities.</p>
          </div>
          <div className="sm:border-l sm:border-[#1F4D2E]/10 sm:pl-8">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E0A526] mb-2">Mercy Made</p>
            <p className="text-[#4F6B54] leading-relaxed text-[15px]">A separate, purpose-driven social enterprise creating quality products, jobs and skills. It is not part of Community Impact.</p>
            <Link to="/mercy-made" data-testid="ci-mercymade-link" className="mt-3 inline-flex items-center gap-2 text-[#1F4D2E] font-semibold hover:text-[#C1272D] transition-colors">Explore Mercy Made <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1F4D2E] text-white" data-testid="ci-cta">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 text-center">
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">Help us build lasting change.</h2>
          <p className="text-[#E7EDE2]/90 mt-5 max-w-2xl mx-auto leading-relaxed">Whether you give, partner or lend your expertise, you can help Refugium Ethiopia grow from safe homes into thriving communities.</p>
          <div className="flex flex-wrap gap-4 justify-center mt-9">
            <a href={donate} target="_blank" rel="noopener noreferrer" data-testid="ci-donate-btn"
              className="bg-[#C1272D] text-white rounded-full px-8 py-4 font-semibold tracking-wide hover:bg-[#9E1F24] transition-colors inline-flex items-center gap-2"><Heart size={18} fill="white" /> Donate on GoFundMe</a>
            <Link to="/join" data-testid="ci-join-btn"
              className="border border-white/50 rounded-full px-8 py-4 font-semibold tracking-wide hover:bg-white hover:text-[#1F4D2E] transition-colors inline-flex items-center gap-2">Get involved <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
